<template>
  <div id="approval-modal" class="uk-modal-container" uk-modal>
    <div class="uk-modal-dialog competency-approval-modal">
      <div class="uk-modal-header">
        <div class="modal-header-content">
          <div class="modal-title-section">
            <i class="uil-check-circle modal-title-icon"></i>
            <h2 class="uk-modal-title"> {{ $t('general.competency_approval') }}</h2>
          </div>
        </div>
      </div>

      <div v-if="competency" class="uk-modal-body">
        <div class="approval-content">
          <div class="approval-info">
            <div class="info-item">
              <label> {{ $t('general.user') }}:</label>
              <span class="info-value">{{ competency.user_full_name }}</span>
            </div>
            <div class="info-item">
              <label>{{ $t('general.competency') }}:</label>
              <span class="info-value competency-name">{{ competency.competency_name }}</span>
            </div>
            <div class="info-item">
              <label> {{ $t('general.situation') }}:</label>
              <span class="status-badge pending">{{ getStatusText(competency.status) }}</span>
            </div>
          </div>

          <div class="approval-message">
            <p> {{ $t('general.what_you_do_for_this_competency') }} </p>
          </div>
        </div>
      </div>

      <div class="uk-modal-footer">
        <div class="modal-footer-content">
          <button class="uk-button uk-button-default" type="button" @click="closeModal">
            <i class="uil-times"></i>
            {{ $t('general.close') }}
          </button>
          <div class="action-buttons">
            <button :disabled="isApproving || isRejecting" class="uk-button uk-button-danger" type="button"
                    @click="rejectCompetency">
              <i v-if="!isRejecting" class="uil-times"></i>
              <i v-if="isRejecting" class="uil-spinner"></i>
              {{ isRejecting ? '.......' : $t('general.reject') }}
            </button>
            <button :disabled="isApproving || isRejecting" class="uk-button uk-button-primary" type="button"
                    @click="approveCompetency">
              <i v-if="!isApproving" class="uil-check"></i>
              <i v-if="isApproving" class="uil-spinner"></i>
              {{ isApproving ? '.......' : $t('general.approve') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ApprovalModal",
  props: {
    competency: {
      type: Object,
      default: null
    },
    isApproving: {
      type: Boolean,
      default: false
    },
    isRejecting: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    getStatusText(status) {
      switch (status) {
        case 'pending':
          return this.$t('general.pending');
        case 'approved':
          return this.$t('general.approved');
        case 'rejected':
          return this.$t('general.rejected');
        default:
          return '--';
      }
    },

    closeModal() {
      UIkit.modal("#approval-modal").hide();
    },

    async approveCompetency() {
      if (!this.competency) return;

      this.$emit('approve', this.competency);
    },

    async rejectCompetency() {
      if (!this.competency) return;

      this.$emit('reject', this.competency);
    }
  }
};
</script>

<style lang="scss" scoped>
// Approval Modal Styles
.competency-approval-modal {
  max-width: 500px !important;
  width: 85%;

  .uk-modal-header {
    background: #f8f9fa;
    color: #2c3e50;
    border-radius: 8px 8px 0 0;
    padding: 16px 20px;
    border-bottom: 1px solid #e8ecf0;

    .modal-header-content {
      display: flex;
      justify-content: center;
      align-items: center;

      .modal-title-section {
        display: flex;
        align-items: center;
        gap: 10px;

        .modal-title-icon {
          font-size: 18px;
          color: #28a745;
        }

        .uk-modal-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
        }
      }
    }
  }

  .uk-modal-body {
    padding: 20px;
    background: #fafbfc;

    .approval-content {
      .approval-info {
        background: white;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;
        border: 1px solid #e8ecf0;

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f1f3f4;

          &:last-child {
            margin-bottom: 0;
            padding-bottom: 0;
            border-bottom: none;
          }

          label {
            font-size: 13px;
            font-weight: 600;
            color: #6c757d;
            margin: 0;
          }

          .info-value {
            font-size: 14px;
            color: #2c3e50;
            font-weight: 500;

            &.competency-name {
              color: #667eea;
              font-weight: 600;
            }
          }

          .status-badge {
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;

            &.pending {
              background: #fff3cd;
              color: #856404;
              border: 1px solid #ffeaa7;
            }
          }
        }
      }

      .approval-message {
        background: #e7f3ff;
        border: 1px solid #b3d9ff;
        border-radius: 8px;
        padding: 16px;
        text-align: center;

        p {
          margin: 0;
          color: #0066cc;
          font-weight: 500;
          font-size: 14px;
        }
      }
    }
  }

  .uk-modal-footer {
    background: white;
    border-top: 1px solid #e8ecf0;
    padding: 16px 20px;
    border-radius: 0 0 8px 8px;

    .modal-footer-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;

      .uk-button {
        padding: 8px 16px;
        border-radius: 6px;
        font-weight: 600;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;

        &.uk-button-default {
          background: #6c757d;
          color: white;
          border: none;

          &:hover {
            background: #5a6268;
            transform: translateY(-1px);
          }
        }

        &.uk-button-primary {
          background: #28a745;
          color: white;
          border: none;

          &:hover:not(:disabled) {
            background: #218838;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
          }

          &:disabled {
            background: #6c757d;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }
        }

        &.uk-button-danger {
          background: #dc3545;
          color: white;
          border: none;

          &:hover:not(:disabled) {
            background: #c82333;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
          }

          &:disabled {
            background: #6c757d;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }
        }

        i {
          font-size: 12px;
        }
      }

      .action-buttons {
        display: flex;
        gap: 8px;
        align-items: center;
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .competency-approval-modal {
    .uk-modal-body {
      padding: 16px;
    }
  }
}
</style>
